import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, LogOut, Settings, Menu, X, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import './Header.responsive.css';

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  return (
    <header className="header">
      <div className="header-content">
        {/* Mobile Hamburger Button - Left Side */}
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          {showMobileMenu ? <X size={44} /> : <Menu size={44} />}
        </button>

        <Link to="/" className="logo">
          <img
            src="/images/taroca_logo.png"
            alt="Taroca Tarot"
            width={140}
            height={40}
            fetchpriority="high"
            decoding="async"
            style={{
              height: '40px',
              width: 'auto'
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul className="nav-links">
            <li>
              <a 
                href="https://tarotreadingcard.online/"
              >
                {t('nav.home')}
              </a>
            </li>
            <li>
              <a 
                href="https://tarotreadingcard.online/tarot-card-meanings/"
              >
                {t('nav.meanings')}
              </a>
            </li>
            <li>
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''}
              >
                {t('nav.spreads')}
              </Link>
            </li>
            <li>
              <Link 
                to="/diary" 
                className={location.pathname === '/diary' ? 'active' : ''}
              >
                {t('nav.diary')}
              </Link>
            </li>
            <li>
              <a 
                href="https://tarotreadingcard.online/learn-tarot-with-taroca/"
              >
                {t('nav.about')}
              </a>
            </li>
          </ul>
        </nav>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <nav className="mobile-nav" ref={mobileMenuRef}>
            <ul className="mobile-nav-links">
              <li>
                <a 
                  href="https://tarotreadingcard.online/"
                  onClick={closeMobileMenu}
                >
                  {t('nav.home')}
                </a>
              </li>
              <li>
                <a 
                  href="https://tarotreadingcard.online/tarot-card-meanings/"
                  onClick={closeMobileMenu}
                >
                  {t('nav.meanings')}
                </a>
              </li>
              <li>
                <Link 
                  to="/" 
                  className={location.pathname === '/' ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('nav.spreads')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/diary" 
                  className={location.pathname === '/diary' ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('nav.diary')}
                </Link>
              </li>
              <li>
                <a 
                  href="https://tarotreadingcard.online/learn-tarot-with-taroca/"
                  onClick={closeMobileMenu}
                >
                  {t('nav.about')}
                </a>
              </li>
            </ul>
            
            {/* Mobile Menu Bottom Section */}
            <div className="mobile-bottom-section">
              {/* Auth Buttons for Mobile (only show if not authenticated) */}
              {!isAuthenticated && (
                <ul className="mobile-auth-links">
                  <li>
                    <Link 
                      to="/login" 
                      className="mobile-nav-link"
                      onClick={closeMobileMenu}
                    >
                      {t('nav.signin')}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/login?mode=register" 
                      className="mobile-nav-link"
                      onClick={closeMobileMenu}
                    >
                      {t('nav.signup')}
                    </Link>
                  </li>
                </ul>
              )}
              
              {/* Language Switcher at bottom of mobile menu */}
              <div className="mobile-menu-language-switcher">
                <LanguageSwitcher />
              </div>
            </div>
          </nav>
        )}

        <div className="header-auth">
          {isAuthenticated ? (
            <div className="user-menu" ref={userMenuRef}>
              <button
                className="user-menu-toggle"
                onClick={() => setShowUserMenu(!showUserMenu)}
                aria-expanded={showUserMenu}
                aria-haspopup="true"
              >
                <div className="user-avatar">
                  {user?.firstName?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="user-name">
                  {user?.firstName || 'User'}
                </span>
              </button>

              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">
                    <div className="user-info">
                      <div className="user-avatar large">
                        {user?.firstName?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="user-display-name">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className="user-email">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="user-menu-items">
                    <Link
                      to="/diary"
                      className="user-menu-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Home className="menu-icon" />
                      {t('nav.diary')}
                    </Link>
                    <Link
                      to="/profile"
                      className="user-menu-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="menu-icon" />
                      {t('user.profileSettings')}
                    </Link>
                    <button
                      className="user-menu-item logout"
                      onClick={handleLogout}
                    >
                      <LogOut className="menu-icon" />
                      {t('user.signOut')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="header-right">
              <LanguageSwitcher />
              <div className="auth-buttons">
                <Link to="/login" className="login-btn">
                  {t('nav.signin')}
                </Link>
                <Link to="/login?mode=register" className="signup-btn">
                  <span className="desktop-signup-text">{t('nav.signup')}</span>
                  <span className="mobile-signup-icon">
                    <User size={25} />
                  </span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
